/* eslint-disable no-unused-vars */
import { Col, Row, Card, Alert, Form, Button } from 'react-bootstrap';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import Pagination from './../../../../components/shared/Pagination';
import { LoadOrderLines } from '../../../../features/Order/OrderSlice';
import { loaderActions } from '../../../../features/Loader/LoaderSlice';
import AdvanceLinesSearch from '../../../../components/shared/AdvanceSearch/LinesSearch';
import OrderLine from '../../../../components/shared/OrderLine/Index';
import { useCurrentCompanyID } from '../../../../hooks/useCurrentCompany';
import _ from 'lodash';
import { submitMassPOFeedback } from '../../../../Services/Requests/purchase-feedback';

function FeedbackOrders() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const { currentVendor } = useSelector((state) => state.vendor);
  const loading = useSelector((state) => state.loader.open);
  const { linesCount, orderLines } = useSelector((state) => state.order);
  const { isFeedBackLoopEnabled } = useSelector((state) => state.general);
  const [sortedLines, setSortedLines] = useState([]);
  const [filteredLines, setFilteredLines] = useState([]);

  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');

  const companyId = useCurrentCompanyID();

  const [skipPaginate, setSkipPaginate] = useState(() => {
    if (searchParams.get('skipPaginate') && searchParams.get('skipPaginate') === 'true') {
      return true;
    }
    return false;
  });

  useEffect(() => {
    if (isFeedBackLoopEnabled === false) {
      navigate('/');
    }
  }, []);

  useEffect(() => {
    const queryParams = {};
    for (const p of searchParams.keys()) {
      queryParams[p] = searchParams.get(p);
    }
    if (searchParams.get('pageSize')) {
      setPageSize(Number(searchParams.get('pageSize')));
    } else {
      setSearchParams(`?${new URLSearchParams({ ...queryParams, pageSize: 10 })}`);
    }
    if (searchParams.get('currentPage')) {
      setCurrentPage(Number(searchParams.get('currentPage')));
    } else {
      setSearchParams(`?${new URLSearchParams({ ...queryParams, currentPage })}`);
    }
  }, []);

  useEffect(() => {
    if (orderLines.length > 0) {
      const sortedLines = _.orderBy(orderLines, ['approvable'], 'desc');
      setSortedLines(sortedLines);
    }
  }, [orderLines]);

  useEffect(() => {
    updateParams();
    if (isFeedBackLoopEnabled === true) {
      fetchPendingOrdersLinesList();
    }
  }, [currentVendor, pageSize, currentPage, skipPaginate, isFeedBackLoopEnabled]);

  useEffect(() => {
    const filteredLines = sortedLines.filter(
      (line) =>
        line.itemNo.toLowerCase().includes(search.trim().toLowerCase()) ||
        line.documentNo.toLowerCase().includes(search.trim().toLowerCase()) ||
        line.feedbackUser.toLowerCase().includes(search.trim().toLowerCase())
    );
    setFilteredLines(filteredLines);
  }, [sortedLines, search]);

  async function updateParams() {
    const queryParams = {};
    for (const p of searchParams.keys()) {
      queryParams[p] = searchParams.get(p);
    }
    queryParams.skipPaginate = skipPaginate;

    setSearchParams(`?${new URLSearchParams({ ...queryParams, pageSize, currentPage })}`);
  }

  async function fetchPendingOrdersLinesList() {
    const vendorFilter = `whseVendorCode eq '${currentVendor.vendNo}'`;
    await dispatch(loaderActions.show());
    await setSortedLines([]);
    await dispatch(
      LoadOrderLines({
        endpoint: '/purchase_order_lines',
        companyId,
        filter: `feedbackStatus eq 'Pending' and ${vendorFilter}`,
        currentPage,
        pageSize,
        skipPaginate,
        implementPaginate: true
      })
    );
    await dispatch(loaderActions.hide());
  }

  function refreshPage() {
    if (isFeedBackLoopEnabled === true) {
      fetchPendingOrdersLinesList();
    }
  }

  useEffect(() => {
    if (isFeedBackLoopEnabled === true) {
      fetchPendingOrdersLinesList();
    }
  }, [companyId, isFeedBackLoopEnabled]);

  const ErrorCard = (
    <>
      <Alert variant="warning">
        {currentVendor !== undefined ? (
          `No Pending Feedback PO Lines for ${currentVendor.vendName}`
        ) : (
          <> No Pending Feedback PO Lines</>
        )}
      </Alert>
    </>
  );

  const SearchErrorCard = (
    <>
      <Alert variant="warning">
        <>
          {' '}
          No PO Lines matching current search{' '}
          <AdvanceLinesSearch
            query={search}
            type={'purchase'}
            vendorNo={currentVendor.vendNo}
            vendorType={currentVendor.VendorType}
            directLink="/purchase-order"
          />
        </>
      </Alert>
    </>
  );

  const orderLinesList = filteredLines.map((line, index) => {
    let vendorType = currentVendor.VendorType;
    if (vendorType === undefined) {
      vendorType = 'Warehouse';
    }
    return (
      <OrderLine
        documentNo={line.documentNo}
        line={line}
        index={index}
        vendorType={currentVendor.VendorType}
        key={index}
        type="purchase"
        refresh={refreshPage}
        editValue={''}
        showSaveButton={true}
        showOrderLink={true}
        isFeedbackProcess={true}
      />
    );
  });

  const selectedLines = orderLines.filter((line) => line.isSelected);
  const approvableLines = orderLines.filter((line) => line.approvable);

  async function submitFeedback(action) {
    if (selectedLines.length > 0) {
      await submitMassPOFeedback(selectedLines, action, dispatch, companyId, refreshPage);
    } else {
      await submitMassPOFeedback(approvableLines, action, dispatch, companyId, refreshPage);
    }
  }

  return (
    <>
      <Row className="justify-content-between">
        <Col md={{ span: 6 }} sm={{ span: 6 }} xs={{ span: 12 }}>
          {approvableLines.length > 0 && (
            <>
              <Button variant="danger" className="mr-3" onClick={() => submitFeedback('reject')}>
                {selectedLines.length > 0
                  ? `Reject Selected (${selectedLines.length})`
                  : `Reject All (${approvableLines.length})`}
              </Button>
              <Button variant="success" onClick={() => submitFeedback('approve')}>
                {selectedLines.length > 0
                  ? `Approve Selected (${selectedLines.length})`
                  : `Approve All (${approvableLines.length})`}
              </Button>
            </>
          )}
          <Button variant="link" onClick={() => setSkipPaginate(!skipPaginate)}>
            {skipPaginate === true ? 'Apply Pagination' : 'Show All'}
          </Button>
        </Col>
        <Col md={{ span: 3 }} sm={{ span: 4 }} xs={{ span: 12 }}>
          <div className="d-flex">
            <Form.Control
              size="sm"
              type="text"
              className="mr-2"
              placeholder="Search by PO # or SKU or User"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col xl={12} md={12}>
          {filteredLines.length > 0 ? (
            orderLinesList
          ) : orderLines.length > 0 ? (
            SearchErrorCard
          ) : loading ? (
            <></>
          ) : (
            ErrorCard
          )}
          {skipPaginate === false && linesCount > pageSize ? (
            search.trim().length === 0 ? (
              <Card className="shadow-sm pt-2">
                <Card.Body>
                  <Row>
                    <Col>
                      <p>Total Pending PO Lines: {linesCount}</p>
                      <p>
                        Currently Viewing : {(currentPage - 1) * pageSize + 1} to{' '}
                        {currentPage * pageSize > linesCount ? linesCount : currentPage * pageSize}
                      </p>
                    </Col>
                    <Col className="text-center">
                      <Form.Group className="mt-2">
                        <Form.Control
                          as="select"
                          onChange={(e) => {
                            setCurrentPage(1);
                            setPageSize(e.target.value);
                          }}
                        >
                          <option value="10">PO Lines per page: 10</option>
                          <option value="20">PO Lines per page: 20</option>
                          <option value="25">PO Lines per page: 25</option>
                          <option value="50">PO Lines per page: 50</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col className="text-right mt-2">
                      <Pagination
                        className="pagination-bar"
                        currentPage={currentPage}
                        totalCount={linesCount}
                        pageSize={pageSize}
                        onPageChange={(page) => setCurrentPage(page)}
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ) : (
              <></>
            )
          ) : (
            <></>
          )}
        </Col>
      </Row>
    </>
  );
}

export default FeedbackOrders;
